import React, { useState } from 'react';
import { FileSearch, Check, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface Issue {
  id: number;
  claimType: string;
  retailer: string;
  poNumber: string;
  amount: string;
}
const issues: Issue[] = [{
  id: 1,
  claimType: 'Short',
  retailer: 'Dollar General',
  poNumber: 'PO-99821',
  amount: '$480.00'
}, {
  id: 2,
  claimType: 'Price Discrepancy',
  retailer: 'Walmart',
  poNumber: 'PO-88201',
  amount: '$1,250.00'
}, {
  id: 3,
  claimType: 'Damaged Goods',
  retailer: 'Amazon',
  poNumber: 'PO-77432',
  amount: '$320.00'
}, {
  id: 4,
  claimType: 'Allowance',
  retailer: 'Target',
  poNumber: 'PO-66109',
  amount: '$750.00'
}, {
  id: 5,
  claimType: 'High Quantity',
  retailer: 'Dollar General',
  poNumber: 'PO-55882',
  amount: '$2,100.00'
}];
function TooltipButton({
  onClick,
  tooltip,
  className,
  children
}: {
  onClick: () => void;
  tooltip: string;
  className: string;
  children: React.ReactNode;
}) {
  const [show, setShow] = useState(false);
  return <span className="relative inline-flex" data-id="element-337">
      <button onClick={onClick} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)} className={className} data-id="element-338">
        {children}
      </button>
      {show && <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 whitespace-nowrap rounded-lg bg-slate-800 text-white text-xs px-2.5 py-1.5 shadow-lg pointer-events-none" data-id="element-339">
          {tooltip}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-340" />
        </span>}
    </span>;
}
export function FrictionQueue() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());
  const visible = issues.filter(i => !dismissed.has(i.id));
  const allSelected = visible.length > 0 && visible.every(i => selected.has(i.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visible.map(i => i.id)));
    }
  };
  const toggleOne = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const approveSelected = () => {
    setDismissed(prev => new Set([...prev, ...selected]));
    setSelected(new Set());
  };
  const rejectSelected = () => {
    setDismissed(prev => new Set([...prev, ...selected]));
    setSelected(new Set());
  };
  const approveOne = (id: number) => {
    setDismissed(prev => new Set([...prev, id]));
    setSelected(prev => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
  };
  const rejectOne = (id: number) => {
    setDismissed(prev => new Set([...prev, id]));
    setSelected(prev => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
  };
  const anySelected = selected.size > 0;
  return <div className="space-y-6" data-id="element-341">
      <div className="flex items-start justify-between" data-id="element-342">
        <div data-id="element-343">
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-344">
            Needs Review
          </h2>
          <p className="text-slate-500 mt-1" data-id="element-345">
            List of deductions that need your review before they can be
            disputed.
          </p>
        </div>

        {/* Bulk actions */}
        <div className="flex items-center gap-2" data-id="element-346">
          <button disabled={!anySelected} onClick={approveSelected} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${anySelected ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 shadow-sm' : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'}`} data-id="element-347">
            <Check className="w-4 h-4" data-id="element-348" />
            Approve
            {anySelected && <span className="ml-1 bg-white/20 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-349">
                {selected.size}
              </span>}
          </button>
          <button disabled={!anySelected} onClick={rejectSelected} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all ${anySelected ? 'bg-rose-600 text-white border-rose-600 hover:bg-rose-700 shadow-sm' : 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'}`} data-id="element-350">
            <X className="w-4 h-4" data-id="element-351" />
            Reject
            {anySelected && <span className="ml-1 bg-white/20 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-352">
                {selected.size}
              </span>}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-353">
        <div className="overflow-x-auto" data-id="element-354">
          <table className="w-full text-sm text-left" data-id="element-355">
            <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-356">
              <tr data-id="element-357">
                <th className="px-4 py-3 w-10" data-id="element-358">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer" data-id="element-359" />
                </th>
                <th className="px-4 py-3" data-id="element-360">Claim Type</th>
                <th className="px-4 py-3" data-id="element-361">Retailer</th>
                <th className="px-4 py-3" data-id="element-362">PO Number</th>
                <th className="px-4 py-3" data-id="element-363">Amount</th>
                <th className="px-4 py-3 text-right" data-id="element-364">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-365">
              {visible.length > 0 ? visible.map(issue => <tr key={issue.id} className={`transition-colors ${selected.has(issue.id) ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`} data-id="element-366">
                    <td className="px-4 py-4" onClick={e => toggleOne(issue.id, e)} data-id="element-367">
                      <input type="checkbox" checked={selected.has(issue.id)} onChange={() => {}} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer" data-id="element-368" />
                    </td>
                    <td className="px-4 py-4" data-id="element-369">
                      <div className="flex items-center gap-2" data-id="element-370">
                        <span className="font-medium text-slate-900" data-id="element-371">
                          {issue.claimType}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-slate-600" data-id="element-372">
                      {issue.retailer}
                    </td>
                    <td className="px-4 py-4 text-slate-600 font-mono text-xs" data-id="element-373">
                      {issue.poNumber}
                    </td>
                    <td className="px-4 py-4 font-semibold text-slate-900" data-id="element-374">
                      {issue.amount}
                    </td>
                    <td className="px-4 py-4" data-id="element-375">
                      <div className="flex items-center justify-end gap-2" data-id="element-376">
                        <TooltipButton tooltip="Approve" onClick={() => approveOne(issue.id)} className="p-1.5 rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors" data-id="element-377">
                          <Check className="w-4 h-4" data-id="element-378" />
                        </TooltipButton>
                        <TooltipButton tooltip="Reject" onClick={() => rejectOne(issue.id)} className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" data-id="element-379">
                          <X className="w-4 h-4" data-id="element-380" />
                        </TooltipButton>
                        <button onClick={() => navigate('/workbench-review')} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 bg-white hover:bg-slate-50 transition-colors whitespace-nowrap" data-id="element-381">
                          <FileSearch className="w-3.5 h-3.5" data-id="element-382" />
                          Review Manually
                        </button>
                      </div>
                    </td>
                  </tr>) : <tr data-id="element-383">
                  <td colSpan={6} className="px-6 py-10 text-center text-slate-400 text-sm" data-id="element-384">
                    All items have been reviewed.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}